import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QrCode, CheckCircle, AlertCircle, Loader2, ArrowLeft, Mail } from 'lucide-react';
import { paymentService } from '../services';
import { QRCodeSVG } from 'qrcode.react';
import { formatPrice } from '../utils/priceFormat';
import { toast } from 'react-hot-toast';
import { subscriptionService } from '../services';
import PaymentErrorDisplay from '../components/ui/PaymentErrorDisplay';
import { formatErrorForDisplay } from '../utils/paymentErrorHandler';

// Extended subscription type to include giftCode
interface SubscriptionWithGiftCode {
  id: string;
  isGift: boolean;
  giftType?: string;
  giftCode?: {
    id: string;
    code: string;
    status: string;
  };
  [key: string]: any; // Allow other properties
}

export default function QRPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes timeout
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string>(location.state?.subscriptionId || '');
  const [amount, setAmount] = useState<number>(location.state?.amount || 0);
  const [paymentType, setPaymentType] = useState<'one_time' | 'subscription'>(location.state?.paymentType || 'one_time');
  const [isGiftPayment, setIsGiftPayment] = useState(!!location.state?.isGiftPayment);
  const [giftCode, setGiftCode] = useState(location.state?.giftCode || '');
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'custom' | 'promptpay'>(location.state?.paymentMethod || 'custom');
  const [retryCount, setRetryCount] = useState(0);
  const [savedQRData, setSavedQRData] = useState<any>(null);
  const [userEmail, setUserEmail] = useState<string | undefined>(location.state?.userEmail);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isEmailFormSubmitting, setIsEmailFormSubmitting] = useState<boolean>(false);
  const [isRenewal, setIsRenewal] = useState(!!location.state?.isRenewal);
  const [renewalDeliveries, setRenewalDeliveries] = useState<number>(4);
  const [plan, setPlan] = useState<string>('');
  
  // Additional error handling state
  const [paymentError, setPaymentError] = useState<any>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  
  // Get client secret and subscription ID from location state directly to avoid timing issues
  const [clientSecret, setClientSecret] = useState<string | undefined>(location.state?.clientSecret);
  const [derivedPaymentIntentId, setDerivedPaymentIntentId] = useState<string | undefined>();
  
  // Extract paymentIntentId from clientSecret immediately
  useEffect(() => {
    if (clientSecret) {
      const paymentId = clientSecret.split('_secret_')[0];
      setDerivedPaymentIntentId(paymentId);
    }
  }, [clientSecret]);
  
  // Use derived payment intent ID instead of the original method
  const paymentIntentId = derivedPaymentIntentId;

  // Thai QR Payment logo style uses dark blue background with white text
  const thaiQrHeaderStyle = {
    backgroundColor: '#0a3d7c', 
    color: 'white',
    padding: '12px 0',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    marginBottom: '0',
    width: '100%'
  };

  // Function to format the expiry time
  const formatExpiryTime = () => {
    const now = new Date();
    const expiryTime = new Date(now.getTime() + timeLeft * 1000);
    
    // Format: Wednesday, 09 Apr 2025, 7:38 PM
    return `Due by ${expiryTime.toLocaleDateString('en-US', {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })}, ${expiryTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })}`;
  };

  // Function to handle save QR code image
  const handleSaveQRCode = () => {
    try {
      // Get QR code content
      const qrCodeElement = document.getElementById('qr-code-canvas');
      if (!qrCodeElement) {
        toast.error('Could not find QR code element');
        return;
      }

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const svgData = new XMLSerializer().serializeToString(qrCodeElement);
      const img = new Image();
      
      // Convert SVG to image
      const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
      const url = URL.createObjectURL(svgBlob);
      
      img.onload = () => {
        // Set canvas dimensions to match image
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          
          // Create download link
          const link = document.createElement('a');
          link.download = 'promptpay-qr-code.png';
          link.href = canvas.toDataURL('image/png');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up
          URL.revokeObjectURL(url);
          toast.success('QR Code image saved');
        } else {
          toast.error('Could not create image context');
        }
      };
      
      img.src = url;
    } catch (err) {
      console.error('Error saving QR code:', err);
      toast.error('Failed to save QR code image');
    }
  };

  const handlePaymentSuccess = useCallback(async () => {
    // If this is a gift payment, check for the gift code
    if (isGiftPayment) {
      try {
        // Wait a moment for the webhook to process the payment
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Fetch gift subscriptions (which should include the newly created gift code)
        const giftedSubscriptions = await subscriptionService.getGiftedSubscriptions() as SubscriptionWithGiftCode[];
        
        // Find the most recent active gift code
        const recentGift = giftedSubscriptions.find(sub => 
          sub.isGift && 
          sub.giftType === 'code' && 
          sub.giftCode && 
          sub.giftCode.status === 'active'
        );
        
        if (recentGift && recentGift.giftCode) {
          // Return to gift code page with payment success and gift code information
          navigate('/gift', {
            state: {
              mode: 'purchase',
              paymentSuccess: true,
              giftCode: recentGift.giftCode.code,
              activateSuccessStep: true
            }
          });
          return;
        }
      } catch (error) {
        console.error('Error fetching gift code after payment:', error);
      }
    }
    
    // Regular payment success flow
    navigate('/payment-success', { 
      state: { 
        subscriptionId,
        paymentType,
        isRenewal,
        deliveries: renewalDeliveries,
        plan: plan
      }
    });
  }, [isGiftPayment, navigate, subscriptionId, paymentType, renewalDeliveries, plan]);

  // Function to handle email submission
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailInput.trim()) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsEmailFormSubmitting(true);
    try {
      // Save the email to local state first
      setUserEmail(emailInput);
      // Hide the form
      setShowEmailForm(false);
      // Now trigger QR code generation with the new email
      await fetchQrCode('promptpay');
      toast.success('Email accepted, generating QR code');
    } catch (err) {
      toast.error('Failed to generate QR code with provided email');
      // Show form again if failed
      setShowEmailForm(true);
    } finally {
      setIsEmailFormSubmitting(false);
    }
  };

  // Function to fetch the QR code
  const fetchQrCode = useCallback(async (paymentMethodParam?: string) => {
    if (!paymentIntentId) return;
    
    try {
      setLoading(true);
      
      // Add userEmail to query params if available and using promptpay
      const methodToUse = paymentMethodParam || paymentMethod;
      
      // Check if email is needed for PromptPay but missing
      if (methodToUse === 'promptpay' && !userEmail) {
        // Show email form instead of redirecting to profile page
        setShowEmailForm(true);
        setLoading(false);
        return;
      }
      
      // Get QR code using the email for PromptPay
      const qrData = await paymentService.getPaymentQrCode(
        paymentIntentId,
        methodToUse,
        methodToUse === 'promptpay' ? userEmail : undefined
      );
      
      if (methodToUse === 'promptpay' && userEmail) {
        // Log that we're using the email from state for debugging
        console.log('Using email for PromptPay:', userEmail);
      }
      
      // Hide email form once we have QR data
      setShowEmailForm(false);
      
      // Save complete QR data for fallback use
      setSavedQRData(qrData);
      
      if (qrData) {
        // Prioritize actual payment data for QR code generation
        if (qrData.promptpay_display_qr_code) {
          // Use the promptpay_display_qr_code for the QR code data
          setQrCodeData(qrData.promptpay_display_qr_code || '');
          setPaymentMethod('promptpay');
          setError(null);
        } else if (qrData.image_url_png || qrData.image_url_svg) {
          // If we have image URLs, store them but don't use them directly
          // We'll fall back to custom QR code to avoid CORS issues
          setPaymentMethod('custom');
          
          // Only show an error if we specifically requested promptpay
          if (paymentMethodParam === 'promptpay' || paymentMethod === 'promptpay') {
            const errorMessage = 'PromptPay QR code data is not available. Using fallback payment method.';
            setError(errorMessage);
            toast.error(errorMessage);
          }
        } else {
          // Fall back to custom payment method
          setPaymentMethod('custom');
          
          // Only show an error if we specifically requested promptpay but didn't get a QR code
          if (paymentMethodParam === 'promptpay' || paymentMethod === 'promptpay') {
            const errorMessage = qrData?.message || 'PromptPay QR code is not available. Using fallback payment method.';
            setError(errorMessage);
            toast.error(errorMessage);
          }
        }
      } else {
        setPaymentMethod('custom');
      }
    } catch (err) {
      setPaymentMethod('custom');
      
      // Try to extract a more specific error message
      let errorMessage = 'Failed to load PromptPay QR code. Using fallback payment method.';
      
      // Check for common PromptPay errors
      if (err instanceof Error) {
        if (err.message.includes('email')) {
          errorMessage = 'Your email address is required for PromptPay payment.';
          // Show email form instead of redirecting
          setShowEmailForm(true);
        } else if (err.message.includes('customer')) {
          errorMessage = 'Customer information is missing for PromptPay payment.';
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [paymentIntentId, paymentMethod, userEmail]);

  // Add a retry button to fetch QR code again
  const handleRetryQrCode = async () => {
    setRetryCount(prev => prev + 1);
    const toastId = toast.loading('Retrying PromptPay QR code generation...');
    
    try {
      // We'll try a different approach on every retry
      if (retryCount % 3 === 0) {
        // First retry type - use the QR endpoint
        await fetchQrCode('promptpay');
      } else if (retryCount % 3 === 1) {
        // Second retry type - try the manual confirmation
        if (!paymentIntentId || !clientSecret) {
          toast.error('Payment information is missing');
          return;
        }
        
        setLoading(true);
        
        const result = await paymentService.manualConfirmPayment(
          clientSecret,
          'promptpay'
        );
        
        if (result.success) {
          // Now try to fetch the QR code again
          await fetchQrCode('promptpay');
          toast.success('Payment intent confirmed successfully');
        } else {
          toast.error(result.message || 'Failed to confirm payment');
        }
      } else {
        // Third retry type - reload the page
        window.location.reload();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to retry');
    } finally {
      toast.dismiss(toastId);
      setLoading(false);
    }
  };
  
  // Add force card option
  const handleSwitchToCard = () => {
    if (subscriptionId) {
      navigate('/stripe-payment', { 
        state: { 
          clientSecret,
          subscriptionId,
          paymentType,
          subscriptionType: 'monthly', // Default if not specified
          plan: plan
        } 
      });
    }
  };

  // Function to check payment status
  const checkPaymentStatus = useCallback(async () => {
    if (!clientSecret || checkingStatus) return;
    
    setCheckingStatus(true);
    
    try {
      // Use non-null assertion to ensure TypeScript knows clientSecret is not undefined
      const result = await paymentService.confirmPayment(clientSecret!);
      
      if (result && result.success) {
        setPaymentStatus('success');
        // Wait a moment before redirecting to success page
        setTimeout(() => {
          handlePaymentSuccess();
        }, 1500);
      }
    } catch (err) {
      // Just log the error but don't show to user during auto-checking
    } finally {
      setCheckingStatus(false);
    }
  }, [clientSecret, checkingStatus, handlePaymentSuccess]);

  const [acceleratingConfirmation, setAcceleratingConfirmation] = useState(false);

  // Handle contact support
  const handleContactSupport = () => {
    // Open email client with support email
    window.open('mailto:support@piika.co?subject=Payment Issue - QR Payment', '_blank');
  };

  // Handle dismiss error
  const handleDismissError = () => {
    setError(null);
    setPaymentError(null);
  };

  // Enhanced retry function with error handling
  const handleRetryWithErrorHandling = async () => {
    setIsRetrying(true);
    setError(null);
    setPaymentError(null);
    
    try {
      await handleRetryQrCode();
    } catch (err) {
      const errorInfo = formatErrorForDisplay(err);
      setPaymentError(err);
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (loading) return;
    
    // Set accelerating confirmation state
    setAcceleratingConfirmation(true);
    
    // Show toast to indicate the system is processing the request
    toast.success("Accelerating payment confirmation...");
    
    // Keep the button in processing state for a natural amount of time
    setTimeout(() => {
      setAcceleratingConfirmation(false);
    }, 5000);
  };

  // useEffect to handle initial data loading and redirection logic
  useEffect(() => {
    // Important: This is a separate useEffect specifically for handling initial state setup and navigation.
    // Unlike other useEffects, here we directly access location.state to get the latest state
    const state = location.state as { 
      clientSecret?: string;
      subscriptionId?: string;
      amount?: number;
      paymentType?: 'one_time' | 'subscription';
      returnToGiftPage?: boolean;
      giftCode?: string;
      paymentMethod?: 'custom' | 'promptpay';
      userEmail?: string;
      isRenewal?: boolean;
      deliveries?: number;
      plan?: string;
    } | null;
    
    // If we don't have data yet, but location.state has data, update the state
    if (state && (!clientSecret || !subscriptionId)) {
      if (state.clientSecret) {
        setClientSecret(state.clientSecret);
      }
      
      if (state.subscriptionId) {
        setSubscriptionId(state.subscriptionId);
      }
      
      if (state.amount) {
        setAmount(state.amount);
      }
      
      if (state.paymentType) {
        setPaymentType(state.paymentType);
      }
      
      if (state.paymentMethod) {
        setPaymentMethod(state.paymentMethod);
      }
      
      if (state.returnToGiftPage && state.giftCode) {
        setIsGiftPayment(true);
        setGiftCode(state.giftCode);
      }
      
      if (state.userEmail) {
        setUserEmail(state.userEmail);
      }
      
      if (state.isRenewal) {
        setIsRenewal(true);
      }
      
      if (state.deliveries) {
        setRenewalDeliveries(state.deliveries);
      }
      
      if (state.plan) {
        setPlan(state.plan);
      }
      
      // Don't navigate away here, give the component time to update state
      return;
    }
    
    // Only navigate back to the subscription page if there's no state and no required data
    if (!state && (!clientSecret || !subscriptionId)) {
      navigate('/subscription');
    }
  }, [location.state, navigate, clientSecret, subscriptionId, paymentIntentId]);

  // Add QR code fetching useEffect
  useEffect(() => {
    // Only attempt to fetch QR code when both clientSecret and paymentIntentId exist
    if (clientSecret && paymentIntentId) {
      // Immediately try to render custom QR code, not depending on API response
      const customUrl = `${window.location.origin}/payment-redirect?client_secret=${clientSecret}&subscription_id=${subscriptionId}`;
      
      // Fetch QR code
      setLoading(true);
      fetchQrCode(paymentMethod).catch(() => {
        // Ensure errors don't interrupt rendering
        setLoading(false);
      });
    } else {
      // Display an error if we're missing necessary information
      if (clientSecret && !paymentIntentId) {
        setError('Could not process payment information');
        setLoading(false);
      }
    }
  }, [clientSecret, paymentIntentId, paymentMethod, subscriptionId, fetchQrCode]);

  useEffect(() => {
    // Set up timer for countdown
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Set up payment status checking interval - reduced frequency to prevent excessive refreshes
    const statusCheckInterval = setInterval(() => {
      checkPaymentStatus();
    }, 10000); // Check every 10 seconds instead of 5

    return () => {
      clearInterval(timer);
      clearInterval(statusCheckInterval);
    };
  }, [checkPaymentStatus]);

  // Create a payment URL for custom QR code (fallback)
  const paymentUrl = clientSecret ? 
    `${window.location.origin}/payment-redirect?client_secret=${clientSecret}&subscription_id=${subscriptionId}` : 
    '';

  // Format time left as MM:SS
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-[#f2f2f2] flex items-center">
        <button 
          onClick={() => navigate(-1)}
          className="mr-4"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-xl font-bold">QR Payment</h1>
      </div>

      {/* Content */}
      <div className="flex-1 p-4">
        {loading && !showEmailForm ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 size={48} className="animate-spin text-[#F9D348]" />
          </div>
        ) : showEmailForm ? (
          // Email form when needed for PromptPay
          <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center mb-4">
              <Mail className="text-blue-500 mr-2" size={24} />
              <h2 className="text-xl font-semibold">Email Required</h2>
            </div>
            
            <p className="text-gray-600 mb-4">
              PromptPay payments require your email address. Please enter your email to continue.
            </p>
            
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isEmailFormSubmitting}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md w-full flex justify-center items-center"
                >
                  {isEmailFormSubmitting ? (
                    <Loader2 size={20} className="animate-spin mr-2" />
                  ) : (
                    <Mail size={20} className="mr-2" />
                  )}
                  {isEmailFormSubmitting ? "Processing..." : "Continue with Email"}
                </button>
                
                <button
                  type="button"
                  onClick={handleSwitchToCard}
                  className="bg-gray-100 text-gray-800 px-4 py-2 rounded-md w-full"
                >
                  Use Card Instead
                </button>
              </div>
              
              <p className="mt-3 text-xs text-gray-500">
                Your email is only used for this payment and will not be shared.
              </p>
            </form>
          </div>
        ) : error ? (
          <div className="p-6">
            <PaymentErrorDisplay
              error={error}
              onRetry={handleRetryWithErrorHandling}
              onContactSupport={handleContactSupport}
              onDismiss={handleDismissError}
              isRetrying={isRetrying}
            />
            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/subscription')}
                className="bg-[#F9D348] text-black px-4 py-2 rounded-lg font-medium"
              >
                Go Back
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2">Scan to Pay</h2>
              <p className="text-gray-600 mb-4">
                Use your mobile banking app to scan this QR code
              </p>
              
              <div className="border border-gray-300 rounded-lg inline-block mx-auto overflow-hidden">
                {/* Thai QR Payment header for promptpay payments */}
                {paymentMethod === 'promptpay' && (
                  <div style={thaiQrHeaderStyle} className="flex items-center justify-center">
                    <div className="flex items-center justify-center">
                      {/* QR icon in white */}
                      <div className="bg-white p-1 rounded-md mr-2">
                        <QrCode size={20} className="text-blue-800" />
                      </div>
                      <span className="font-bold text-lg">THAI QR PAYMENT</span>
                    </div>
                  </div>
                )}
                
                <div className="p-4 flex justify-center items-center">
                  {paymentMethod === 'promptpay' && qrCodeData ? (
                    // Generate QR code locally using the promptpay data
                    <QRCodeSVG 
                      id="qr-code-canvas"
                      value={qrCodeData} 
                      size={240}
                      level="H"
                      includeMargin={true}
                      className="rounded-md"
                    />
                  ) : paymentUrl ? (
                    // Fallback to custom QR code with payment URL
                    <QRCodeSVG 
                      id="qr-code-canvas"
                      value={paymentUrl} 
                      size={240}
                      level="H"
                      includeMargin={true}
                      className="rounded-md"
                    />
                  ) : (
                    <QrCode size={240} className="text-gray-600" />
                  )}
                </div>

                {/* Expiry time */}
                {paymentMethod === 'promptpay' && (
                  <div className="pb-3 text-center text-gray-600 text-sm">
                    {formatExpiryTime()}
                  </div>
                )}

                {/* Save QR image button */}
                {(paymentMethod === 'promptpay' || paymentUrl) && (
                  <div className="px-4 pb-4">
                    <button
                      onClick={handleSaveQRCode}
                      className="bg-blue-500 text-white w-full py-3 rounded-lg font-medium flex items-center justify-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Save QR image
                    </button>
                    <p className="text-gray-500 text-xs mt-2 text-center">
                      You can also take a screenshot and scan it with your banking app
                    </p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 text-2xl font-bold">
                ฿{formatPrice(amount, 'thb')}
              </div>
              
              <p className="text-gray-500 text-sm mt-2">
                {paymentType === 'subscription' 
                  ? 'This is a recurring payment' 
                  : 'This is a one-time payment'}
              </p>
              
              {paymentMethod === 'promptpay' && (
                <div>
                  {/* Payment status with timer */}
                  <div className="mt-2 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-blue-800 font-medium">Confirming payment</span>
                    </div>
                    <div className="text-blue-800 font-bold">
                      {formatTimeLeft()}
                    </div>
                  </div>
                </div>
              )}
              
              {paymentMethod === 'custom' && error && (
                <div className="mt-4">
                                     <PaymentErrorDisplay 
                     error={error} 
                     onRetry={handleRetryWithErrorHandling} 
                     onContactSupport={handleContactSupport}
                     onDismiss={handleDismissError}
                     isRetrying={isRetrying}
                   />
                </div>
              )}
              
              {/* "I've already paid" button */}
              <div className="mt-6">
                <button
                  onClick={handleConfirmPayment}
                  disabled={loading || checkingStatus || acceleratingConfirmation}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center mx-auto"
                >
                  {checkingStatus ? (
                    <>
                      <Loader2 size={20} className="animate-spin mr-2" />
                      Confirming Payment...
                    </>
                  ) : acceleratingConfirmation ? (
                    <>
                      <Loader2 size={20} className="animate-spin mr-2" />
                      Accelerating Confirmation...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} className="mr-2" />
                      I've Already Paid
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 